import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Heart, HeartHandshake, Search, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const LandingPage = () => {
  const navigate = useNavigate();

  const handleLogin = () => {
    navigate('/login');
  };

  const handleDonate = () => {
    // TODO: Integrar con API de DonarOnline
    // window.open('https://donaronline.net/pawfinder/...', '_blank');
    toast.info('Próximamente podrás donar a través de DonarOnline. ¡Gracias por tu interés!');
  };

  return (
    <div className="min-h-screen gradient-mesh">
      {/* Header */}
      <header className="container mx-auto px-4 py-6 flex justify-between items-center gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Heart className="h-7 w-7 sm:h-8 sm:w-8 text-primary shrink-0" fill="currentColor" />
          <h1 className="text-xl sm:text-2xl font-fraunces font-semibold text-foreground truncate">MiAmigoFiel</h1>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          <Button
            data-testid="donate-header-button"
            onClick={() => document.getElementById('donar')?.scrollIntoView({ behavior: 'smooth' })}
            variant="outline"
            className="rounded-full px-3 sm:px-6 py-2 border-2 border-primary text-primary hover:bg-primary/10"
          >
            <HeartHandshake className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Donar</span>
          </Button>
          <Button
            data-testid="login-button"
            onClick={handleLogin}
            className="btn-primary rounded-full px-3 sm:px-6 py-2 bg-primary text-primary-foreground hover:bg-primary/90"
          >
            Iniciar Sesión
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="container mx-auto px-4 py-12 sm:py-16 md:py-24">
        <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
          <div className="space-y-6">
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-fraunces font-semibold leading-tight text-foreground">
              Reuniendo mascotas con sus familias
            </h2>
            <p className="text-lg text-muted-foreground">
              Usa inteligencia artificial y una comunidad solidaria para encontrar mascotas perdidas y ayudar a reunir familias.
            </p>
            <div className="flex gap-4 flex-wrap">
              <Button
                data-testid="get-started-button"
                onClick={handleLogin}
                size="lg"
                className="btn-primary rounded-full px-8 py-6 text-lg bg-primary text-primary-foreground hover:bg-primary/90"
              >
                Comenzar Ahora
              </Button>
              <Button
                data-testid="browse-button"
                onClick={() => navigate('/lost-pets')}
                size="lg"
                variant="outline"
                className="rounded-full px-8 py-6 text-lg border-2"
              >
                Ver Mascotas Perdidas
              </Button>
            </div>
          </div>
          <div className="relative">
            <div className="rounded-3xl overflow-hidden shadow-hover">
              <img
                src="https://images.unsplash.com/photo-1702337446616-b48157bfc165?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHxkb2clMjByZXVuaW9uJTIwaGFwcHklMjBvd25lcnxlbnwwfHx8fDE3Njk5NTI2OTJ8MA&ixlib=rb-4.1.0&q=85"
                alt="Happy reunion dog and owner"
                className="w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="container mx-auto px-4 py-12 sm:py-16">
        <h3 className="text-3xl sm:text-4xl font-fraunces font-semibold text-center mb-8 sm:mb-12 text-foreground">
          Cómo funciona
        </h3>
        <div className="grid md:grid-cols-3 gap-6 md:gap-8">
          <div className="bg-card rounded-3xl p-8 shadow-card card-hover border border-border">
            <div className="bg-primary/10 rounded-2xl w-14 h-14 flex items-center justify-center mb-4">
              <Heart className="h-7 w-7 text-primary" />
            </div>
            <h4 className="text-xl font-fraunces font-semibold mb-3 text-card-foreground">Publicar</h4>
            <p className="text-muted-foreground">
              Publica una mascota perdida o encontrada con fotos y detalles. Nuestro sistema la analiza automáticamente.
            </p>
          </div>
          <div className="bg-card rounded-3xl p-8 shadow-card card-hover border border-border">
            <div className="bg-accent/20 rounded-2xl w-14 h-14 flex items-center justify-center mb-4">
              <Sparkles className="h-7 w-7 text-accent" />
            </div>
            <h4 className="text-xl font-fraunces font-semibold mb-3 text-card-foreground">Coincidir</h4>
            <p className="text-muted-foreground">
              La IA analiza fotos y detalles para encontrar coincidencias potenciales entre mascotas perdidas y encontradas.
            </p>
          </div>
          <div className="bg-card rounded-3xl p-8 shadow-card card-hover border border-border">
            <div className="bg-secondary/20 rounded-2xl w-14 h-14 flex items-center justify-center mb-4">
              <Search className="h-7 w-7 text-secondary-foreground" />
            </div>
            <h4 className="text-xl font-fraunces font-semibold mb-3 text-card-foreground">Reunir</h4>
            <p className="text-muted-foreground">
              Conecta con los dueños y ayuda a reunir mascotas con sus familias. ¡Cada reencuentro cuenta!
            </p>
          </div>
        </div>
      </section>

      {/* Donation Section */}
      <section id="donar" data-testid="donate-section" className="container mx-auto px-4 py-12 sm:py-16">
        <div className="bg-accent/10 rounded-3xl p-6 sm:p-10 lg:p-12 text-center border border-accent/30">
          <div className="bg-accent/20 rounded-2xl w-16 h-16 flex items-center justify-center mb-6 mx-auto">
            <HeartHandshake className="h-8 w-8 text-accent" />
          </div>
          <h3 className="text-3xl sm:text-4xl font-fraunces font-semibold mb-4 text-foreground">
            Ayudanos a seguir reuniendo familias
          </h3>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            Tu donación nos ayuda a mantener la plataforma activa, cubrir costos de
            inteligencia artificial y llegar a más mascotas perdidas.
          </p>
          <Button
            data-testid="donate-button"
            size="lg"
            onClick={handleDonate}
            className="btn-primary rounded-full px-8 py-6 text-lg bg-primary text-primary-foreground hover:bg-primary/90"
          >
            <HeartHandshake className="h-5 w-5 mr-2" />
            Donar Ahora
          </Button>
        </div>
      </section>

      {/* CTA Section */}
      <section className="container mx-auto px-4 py-12 sm:py-16 mb-12 sm:mb-16">
        <div className="bg-primary rounded-3xl p-6 sm:p-10 lg:p-12 text-center shadow-hover">
          <h3 className="text-3xl sm:text-4xl font-fraunces font-semibold mb-4 text-primary-foreground">
            ¿Perdiste o encontraste una mascota?
          </h3>
          <p className="text-lg text-primary-foreground/90 mb-6 max-w-2xl mx-auto">
            Únete a nuestra comunidad compasiva y comienza tu búsqueda ahora.
          </p>
          <Button
            data-testid="cta-button"
            onClick={handleLogin}
            size="lg"
            className="rounded-full px-8 py-6 text-lg bg-white text-primary hover:bg-white/90"
          >
            Comenzar Gratis
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black text-white">
        <div className="container mx-auto px-4 py-8 text-center space-y-2">
          <p className="text-sm sm:text-base font-medium">
            Fundación Gates, aplicando la tecnología al servicio de la comunidad
          </p>
          <a
            href="https://www.gatesconsulting.net/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-white/70 hover:text-white transition-colors"
          >
            https://www.gatesconsulting.net/
          </a>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;